import { EdugramIconNew } from '@public/static/icons/edugramIcon'
import { useState } from 'react'
// import { YouTubeEmbed } from '@next/third-parties/google'
import styled from '@emotion/styled'
import Image from 'next/image'
import PlayIcon from '@public/static/icons/videoPlayerIcons/playIcon'
import { colors, fontFamilies } from '@configs/styles/config'
import { useTranslation } from 'react-i18next'

type Props = {}

const HeroContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 4rem 1rem;
`

const ContentWrapper = styled.div`
  max-width: 1380px;
  margin: 0 auto;
  text-align: center;
`

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 1rem;
`

const PlatformText = styled.span`
  color: #1a1e3d;
  font-family: ${fontFamilies.regular};
  font-size: 18.8095px;
  flex: none;
  order: 1;
  letter-spacing: 1px;
  flex-grow: 0;
`

const MainHeading = styled.h1`
  font-size: 3rem;
  font-family: ${fontFamilies.bold};
  /* font-family: var(--font-figtree-bold); */
  /* font-weight: 700; */
  color: #1a1e3d;
  margin-bottom: 1.5rem;
  line-height: 1.1;

  @media (min-width: 768px) {
    font-size: 4.5rem;
  }
`

const SubHeading = styled.p`
  font-size: 1.25rem;
  color: #374151;
  margin-bottom: 2rem;
  max-width: 784px;
  font-weight: ${fontFamilies.regular};
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`

const BadgesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
`

const Badge = styled.div`
  background-color: ${colors.uguLightBrightPurpleLanding};
  color: #1a1e3d;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 1rem;
  font-family: ${fontFamilies.medium};
`

const VideoContainer = styled.div`
  max-width: 74rem;
  aspect-ratio: 16 / 9;
  margin: 2rem auto;
  border-radius: 1.5rem;
  @media (min-width: 640px) {
    border-radius: 2.4rem;
  }
  overflow: hidden;
  width: 100%;
  position: relative;
  background-color: black;
`

const PlayButton = styled.button`
  z-index: 8;
  position: absolute;
  background-color: rgba(113, 129, 255, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  inset: 0;
  margin: auto;
  border: none;
  cursor: pointer;
  box-shadow: 0 0 0 10px rgba(113, 129, 255, 0.5);

  @media (min-width: 640px) {
    width: 100px;
    height: 100px;
  }
`

const VideoOverlay = styled.div`
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 9999px;
  font-size: 0.875rem;
  font-family: ${fontFamilies.medium};
  color: white;
  position: absolute;
  inset: 0;
  margin: auto;
  transform: translateY(72px);
  max-width: max-content;
  max-height: max-content;
  height: fit-content;
  padding: 0.625rem 1rem;
  z-index: 8;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (min-width: 640px) {
    transform: translateY(96px);
  }
`

const HeroSection = ({}: Props) => {
  const { t } = useTranslation('homeLanding/herosection')
  const [play, setPlay] = useState(false)
  return (
    <HeroContainer>
      <ContentWrapper>
        {/* Logo and platform text */}
        <LogoSection>
          <span className="mb-[-1.4px]">
            <EdugramIconNew
              iconProps={{ width: '28', height: '28' }}
              textProps={{
                fontSize: '18.8px',
                y: '1.03em',
                x: '1.5em',
              }}
              height="28"
              width="110"
            />
          </span>
          <PlatformText>{t('platformText')}</PlatformText>
        </LogoSection>

        {/* Main heading */}
        <MainHeading>{t('mainHeading')}</MainHeading>

        {/* Subheading */}
        <SubHeading>{t('subHeading')}</SubHeading>

        {/* Feature badges */}
        <BadgesContainer>
          {(t('badges', { returnObjects: true }) as string[]).map((badge) => (
            <Badge key={badge}>{badge}</Badge>
          ))}
        </BadgesContainer>
      </ContentWrapper>

      <VideoContainer className="shadow-xl">
        {play ? (
          <iframe
            className="w-full aspect-video shadow-xl overflow-hidden"
            src="https://www.youtube.com/embed/nYjFa99kv6E?autoplay=1&rel=0&modestbranding=1&showinfo=0"
            title={t('video.alt')}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          ></iframe>
        ) : (
          <>
            <Image
              src={'/static/images/features/edugram-thumbnail.png'}
              width={1000}
              height={825}
              alt={t('video.alt')}
              className="w-full h-full aspect-video sm:w-full sm:h-full"
              priority={true}
              fetchPriority="high"
              // Use lower resolution for mobile, higher for desktop
              sizes="(max-width: 640px) 400px, 1100px"
            />
            <PlayButton
              onClick={() => setPlay(true)}
              aria-label={t('video.ariaLabel')}
            >
              <PlayIcon width="23" height="23" color="white" />
            </PlayButton>
            <VideoOverlay>
              <PlayIcon width="13" height="13" color="white" />
              {t('video.overlay')}
            </VideoOverlay>
          </>
        )}
        {/* <YouTubeEmbed
          videoid="nYjFa99kv6E"
          params="autoplay=1&rel=0&modestbranding=1&showinfo=0"
          width={725}
          height={725}
          style="width: 100%; border-radius: 2.4rem; aspect-ratio: 16 / 9;"
        /> */}
      </VideoContainer>
    </HeroContainer>
  )
}

export default HeroSection
